var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import useApi from '@/hoc/hooks/useApi';
import { UpArrow } from '@/icons';
import { RightArrow } from '@/icons/right-arrow';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../generic-customization-modal';
import { FormField } from '../ui';
var CustomNewEditModal = function (_a) {
    var _b, _c, _d;
    var isOpen = _a.isOpen, mode = _a.mode, selectedItem = _a.selectedItem, onClose = _a.onClose, onSave = _a.onSave;
    var _e = useForm({
        defaultValues: {
            name: '',
            description: '',
            selectedStages: [],
        },
        mode: 'onChange', // Validate on every change
    }), register = _e.register, handleSubmit = _e.handleSubmit, setValue = _e.setValue, reset = _e.reset, errors = _e.formState.errors, setError = _e.setError, clearErrors = _e.clearErrors, getValues = _e.getValues, watch = _e.watch;
    var _f = useState([]), stagesList = _f[0], setStagesList = _f[1];
    var _g = useState({}), expandedStages = _g[0], setExpandedStages = _g[1];
    var _h = useApi('/stage'), fetchData = _h.fetchData, data = _h.data, loading = _h.loading;
    useEffect(function () {
        fetchData();
    }, [fetchData]);
    var watchSelectedStages = watch('selectedStages');
    useEffect(function () {
        var _a;
        var selectedStages = (_a = ((stagesList === null || stagesList === void 0 ? void 0 : stagesList.filter(function (stage) { return stage.isDefault; })) || [])) === null || _a === void 0 ? void 0 : _a.map(function (stage) { return stage._id; });
        setValue('selectedStages', selectedStages, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
    }, [stagesList]);
    useEffect(function () {
        if (data) {
            setStagesList(data);
            // Expand all stages by default
            var defaultExpanded_1 = {};
            data.forEach(function (stage) {
                defaultExpanded_1[stage._id] = true; // Expand parent stages
            });
            setExpandedStages(defaultExpanded_1);
        }
        else {
            fetchData().catch(function (error) { return console.error('Error fetching stage data: ', error); });
        }
    }, [data]);
    useEffect(function () {
        var _a;
        if (isOpen) {
            if (mode === 'edit' && selectedItem) {
                reset({
                    name: selectedItem.name,
                    description: selectedItem.description,
                    selectedStages: selectedItem.stageId || [],
                });
            }
            else if (mode === 'create') {
                // Create mode: set default selected stages from isDefault
                var defaultSelectedStages = (_a = ((stagesList === null || stagesList === void 0 ? void 0 : stagesList.filter(function (stage) { return stage.isDefault; })) || [])) === null || _a === void 0 ? void 0 : _a.map(function (stage) { return stage._id; });
                reset({
                    name: '',
                    description: '',
                    selectedStages: defaultSelectedStages,
                });
            }
        }
        else {
            // If modal is closed, reset form to initial state
            reset();
        }
    }, [isOpen, mode, selectedItem, stagesList, reset]);
    var toggleExpand = function (stageId) {
        setExpandedStages(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[stageId] = !prev[stageId], _a)));
        });
    };
    var handleStageSelection = function (stageId, children) {
        var selectedStages = new Set(getValues('selectedStages') || []);
        if (selectedStages.has(stageId)) {
            // If already selected, remove it and all its children
            selectedStages.delete(stageId);
            children === null || children === void 0 ? void 0 : children.forEach(function (child) { return selectedStages.delete(child._id); });
        }
        else {
            // Select the stage
            selectedStages.add(stageId);
            if (children && children.length > 0) {
                children.forEach(function (child) { return selectedStages.add(child._id); });
            }
        }
        // Parent-Child Selection/Deselection Logic
        stagesList.forEach(function (stage) {
            var _a, _b;
            if ((_a = stage.children) === null || _a === void 0 ? void 0 : _a.some(function (child) { return selectedStages.has(child._id); })) {
                selectedStages.add(stage._id); // Select parent if any child is selected
            }
            else if ((_b = stage.children) === null || _b === void 0 ? void 0 : _b.every(function (child) { return !selectedStages.has(child._id); })) {
                selectedStages.delete(stage._id); // Deselect parent if all children are unselected
            }
        });
        setValue('selectedStages', Array.from(selectedStages));
        // Validate stages after selection
        var currentStages = Array.from(selectedStages);
        if (currentStages.length === 0) {
            setError('selectedStages', {
                type: 'required',
                message: 'At least one stage must be selected',
            });
        }
        else {
            clearErrors('selectedStages');
        }
    };
    var validateName = function (value) {
        if (!value)
            return 'Name is required';
        if (value.length < 3)
            return 'Name must be at least 3 characters';
        if (value.length > 20)
            return 'Name cannot exceed 20 characters';
        return true;
    };
    var validateDescription = function (value) {
        if (value && value.length > 0) {
            if (value.length < 3)
                return 'Description must be at least 3 characters';
            if (value.length > 1000)
                return 'Description cannot exceed 1000 characters';
        }
        return true;
    };
    var onSubmit = function (formData) {
        // Validate name
        var nameValidation = validateName(formData.name);
        if (nameValidation !== true) {
            setError('name', { type: 'validate', message: nameValidation });
            return;
        }
        // Validate description
        var descValidation = validateDescription(formData.description);
        if (descValidation !== true) {
            setError('description', { type: 'validate', message: descValidation });
            return;
        }
        // Validate stages
        if (!formData.selectedStages || formData.selectedStages.length === 0) {
            setError('selectedStages', {
                type: 'required',
                message: 'At least one stage must be selected',
            });
            return;
        }
        var newItem = [
            {
                name: formData.name,
                description: formData.description,
                stageId: formData.selectedStages,
            },
        ];
        if (mode === 'edit') {
            newItem[0]._id = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem._id;
        }
        onSave(newItem);
    };
    return (_jsx(Modal, { title: mode === 'create' ? 'New Customization' : 'Edit Customization', isOpen: isOpen, onClose: onClose, size: 'full', cancelButton: { label: 'Cancel', onClick: onClose }, actionButton: {
            label: mode === 'create' ? 'Create' : 'Update',
            onClick: handleSubmit(onSubmit),
            variant: 'primary',
        }, children: _jsxs("div", { className: 'flex flex-col gap-4 mb-4 md:flex-row', children: [_jsxs("div", { className: 'w-full flex flex-col gap-2 md:w-1/2', children: [_jsx(FormField, { error: (_b = errors.name) === null || _b === void 0 ? void 0 : _b.message, label: 'Name', isRequired: true, tooltip: 'Process Name', containerClassName: 'w-full', children: _jsx("input", __assign({}, register('name', { validate: validateName }), { type: 'text', className: 'w-full border border-vjwSecondary p-2 rounded-lg bg-blue-50', placeholder: 'Name the customized stage' })) }), _jsx(FormField, { error: (_c = errors.description) === null || _c === void 0 ? void 0 : _c.message, label: 'Description (Optional)', tooltip: 'Process Name', containerClassName: 'w-full', children: _jsx("textarea", __assign({}, register('description', { validate: validateDescription }), { className: 'w-full border border-vjwSecondary p-2 rounded-lg bg-blue-50 h-48', placeholder: 'Enter the description' })) })] }), _jsx("div", { className: 'w-full md:w-1/2', children: _jsx(FormField, { error: (_d = errors.selectedStages) === null || _d === void 0 ? void 0 : _d.message, label: 'Select the stages', isRequired: true, containerClassName: 'w-full', children: _jsx("div", { className: 'max-h-50 w-full border border-vjwSecondary rounded-lg p-2 bg-blue-50', children: stagesList.map(function (stage, parentIndex) { return (_jsxs("div", { className: 'mb-2', children: [_jsxs("div", { className: 'flex items-center', children: [_jsx("input", { type: 'checkbox', checked: watchSelectedStages === null || watchSelectedStages === void 0 ? void 0 : watchSelectedStages.includes(stage._id), onChange: function () { return handleStageSelection(stage._id, stage.children); }, className: 'mr-2', disabled: stage.isDefault }), _jsxs("span", { className: 'text-sm', children: [parentIndex + 1, ". ", stage.name] }), stage.children && stage.children.length > 0 && (_jsx("button", { type: 'button', onClick: function () { return toggleExpand(stage._id); }, className: 'ml-2 text-xs text-blue-500', children: expandedStages[stage._id] ? _jsx(UpArrow, {}) : _jsx(RightArrow, {}) }))] }), expandedStages[stage._id] && stage.children && (_jsx("div", { className: 'ml-5 pl-3 border-l-2 border-vjwSecondary', children: stage.children.map(function (subStage, subIndex) { return (_jsxs("div", { className: 'flex items-center mt-1', children: [_jsx("input", { type: 'checkbox', checked: watchSelectedStages === null || watchSelectedStages === void 0 ? void 0 : watchSelectedStages.includes(subStage._id), onChange: function () { return handleStageSelection(subStage._id); }, className: 'mr-2' }), _jsxs("span", { className: 'text-sm', children: [parentIndex + 1, ".", subIndex + 1, " ", subStage.name] })] }, subStage._id)); }) }))] }, stage._id)); }) }) }) })] }) }));
};
export default CustomNewEditModal;
